/**
 * Mention Parser - Resolution Functions
 *
 * Functions for resolving usernames to user IDs via Firestore.
 */

import { collection, query, where, getDocs, limit } from "firebase/firestore";
import { db } from "../../../../firebaseConfig";

/**
 * Resolve a single username to a user ID
 * @param {string} username - Username to look up (without @ prefix)
 * @returns {Promise<{username: string, userId: string} | null>}
 */
export async function resolveUsernameToId(username) {
  if (!username) return null;

  try {
    const usersRef = collection(db, "users");
    const q = query(
      usersRef,
      where("username_lower", "==", username.toLowerCase()),
      limit(1),
    );

    const snapshot = await getDocs(q);

    if (snapshot.empty) {
      return null;
    }

    const userDoc = snapshot.docs[0];
    const data = userDoc.data();
    // Prefer proper-cased username from the doc, fall back to displayName/name
    const properUsername =
      data.username || data.displayName || data.name || username;
    return {
      username: properUsername,
      userId: userDoc.id,
    };
  } catch (error) {
    console.error("Error resolving username:", username, error);
    return null;
  }
}

/**
 * Resolve multiple usernames to user IDs in batch
 * @param {string[]} usernames - Array of usernames (without @ prefix)
 * @returns {Promise<Array<{username: string, userId: string}>>}
 */
export async function resolveMentionsToUserIds(usernames) {
  if (!usernames || usernames.length === 0) {
    return [];
  }

  // Deduplicate and lowercase
  const uniqueUsernames = [...new Set(usernames.map((u) => u.toLowerCase()))];

  // Resolve in parallel
  const results = await Promise.all(
    uniqueUsernames.map((username) => resolveUsernameToId(username)),
  );

  // Filter out nulls (unresolved usernames)
  return results.filter((result) => result !== null);
}
